import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useExaminer } from '../Context/Examiner.context';
import { ExamTaskGroupSolutionDownloadSignalRContext } from '../Context/ExamTaskGroupSolutionDownload.context';
import { useLayoutContext } from '../Context/Layout.context';
import { DownloadExamTaskGroupSolution, ExamTaskGroupSolutionDownloadResult } from '../models/assessment.types';
import { CallbackMethodNames } from '../models/examTaskGroupSolutionDownloadSignalR.types';
import { DownloadType } from '../pages/Assessment/Assessment.types';
import { downloadExamTaskGroupSolution } from '../services/distribution/distribution.service';
import { AssessmentSessionProcessTypeId } from '../services/exams/assessmentSession/assessments.types';
import { CardsDownloaderContainerProps } from './CardsDownloaderContainer.component';

export interface CardsDownloaderProps extends CardsDownloaderContainerProps {
  connected: boolean;
  processTypeId: number;
}

const CardsDownloader: React.FC<CardsDownloaderProps> = (props) => {
  const { examiner } = useExaminer();
  const processTypeId = props.processTypeId as AssessmentSessionProcessTypeId;
  const { openAlert, showLoader } = useLayoutContext();
  const connectionEmergencyTimout = useRef<NodeJS.Timeout | undefined>(undefined);
  const downloadEmergencyTimout = useRef<NodeJS.Timeout | undefined>(undefined);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [standardDownloadMode, setStandardDownloadMode] = useState<boolean>(false);
  const [downloadTypeUsed, setDownloadTypeUsed] = useState<DownloadType>(DownloadType.single);
  const [receivedResponse, setReceivedResponse] = useState<DownloadExamTaskGroupSolution | null>(null);
  const [solutionsIdsToWaitFor, setSolutionsIdsToWaitFor] = useState<string[]>([]);
  const [solutionsIdsReceived, setSolutionsIdsReceived] = useState<string[]>([]);

  ExamTaskGroupSolutionDownloadSignalRContext.useSignalREffect(
    CallbackMethodNames.ExamTaskGroupSolutionScoreProcessed,
    (message: { id: string }) => {
      setSolutionsIdsReceived((prevState) => [...prevState, message.id]);
    },
    [isListening, solutionsIdsToWaitFor],
  );

  const clearConnectionEmergencyTimout = () => {
    if (connectionEmergencyTimout.current) {
      clearTimeout(connectionEmergencyTimout.current);
    }
  };
  const clearDownloadEmergencyTimout = () => {
    if (downloadEmergencyTimout.current) {
      clearTimeout(downloadEmergencyTimout.current);
    }
  };
  const clearEmergencyTimouts = () => {
    clearConnectionEmergencyTimout();
    clearDownloadEmergencyTimout();
  };

  const handleFinishDownloading = async (
    socketNotificationsUsed: boolean,
    response?: DownloadExamTaskGroupSolution,
  ) => {
    if (socketNotificationsUsed) {
      // small delay to let the backend finish all assingment processes
      await new Promise((r) => setTimeout(r, 500));
    }
    if (response) {
      props.onDownloadFinish(downloadTypeUsed, response);
    } else if (receivedResponse) {
      props.onDownloadFinish(downloadTypeUsed, receivedResponse);
    }
    setSolutionsIdsToWaitFor([]);
    setSolutionsIdsReceived([]);
    showLoader(false);
    clearEmergencyTimouts();
    setStandardDownloadMode(false);
  };

  useEffect(() => {
    return () => {
      clearEmergencyTimouts();
    };
  }, []);

  useEffect(() => {
    // notifications may be sent not in a right order, so have to check if all first X solutions arrived
    if (
      solutionsIdsReceived.length > 0 &&
      solutionsIdsToWaitFor.length > 0 &&
      solutionsIdsReceived.length >= solutionsIdsToWaitFor.length
    ) {
      const someIdsNotFound = solutionsIdsToWaitFor.some((requiredId) => !solutionsIdsReceived.includes(requiredId));
      if (!someIdsNotFound) {
        handleFinishDownloading(true);
      }
    }
  }, [solutionsIdsReceived, solutionsIdsToWaitFor]);

  useEffect(() => {
    if (props.connected) {
      clearConnectionEmergencyTimout();
    }
  }, [props.connected, clearConnectionEmergencyTimout]);

  useEffect(() => {
    if ((props.connected || standardDownloadMode) && examiner && examiner.examiner) {
      setIsListening(true);
      downloadExamTaskGroupSolution(processTypeId, downloadTypeUsed, props.assessmentSessionId, examiner.examiner.id)
        .then(async (response: DownloadExamTaskGroupSolution) => {
          setReceivedResponse(response);
          if (!standardDownloadMode) {
            handleFinishDownloading(false, response);
            showLoader(false);

            if (response.solutions.length) {
              setSolutionsIdsToWaitFor(response.solutions.map((item) => item.id).slice(0, props.pageSize));
            }
          } else {
            if (
              ![
                ExamTaskGroupSolutionDownloadResult.SingleSolution,
                ExamTaskGroupSolutionDownloadResult.FilledUpToLimits,
              ].includes(response.solutionDownloadResult)
            ) {
              handleFinishDownloading(false, response);
            } else {
              setTimeout(() => {
                handleFinishDownloading(false, response);
              }, 5000);
            }
          }
        })
        .catch(() => {
          openAlert('error');
          showLoader(false);
        });
    }
  }, [props.connected, downloadTypeUsed, props.assessmentSessionId, examiner, standardDownloadMode]);

  const handleCardsDownload = async (downloadType: DownloadType) => {
    if (examiner && examiner.examiner) {
      showLoader(true, downloadType === DownloadType.fillToLimits ? 'Pobieram wszystkie karty' : 'Pobieram kartę');
      setDownloadTypeUsed(downloadType);
      connectionEmergencyTimout.current = setTimeout(() => {
        setStandardDownloadMode(true);
      }, 3000);

      props.onDownloadStart && props.onDownloadStart();
    }
  };
  return <>{props.onRender(handleCardsDownload)}</>;
};

export default CardsDownloader;
